<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { getHelpUrlBasedOnLocale } from '@@/bits/current_locale'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { OzDropdownButtonColorScheme } from '@@/library/v4/components/OzDropdownButton.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonHrefMode,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import { bridgeUrlClick } from '@@/native_bridge/bridge_url'
import LocaleSelector from '@@/vuecomponents/LocaleSelector.vue'

defineProps<{
  darkMode?: boolean | 'auto'
}>()

const emit = defineEmits<{
  (e: 'open-accessibility-settings'): void
}>()

const helpUrl = getHelpUrlBasedOnLocale()
const handleHelpAndFeedbackButtonClick = (event: MouseEvent) => {
  trackEvent('Access Request', 'Clicked help and feedback button')
  if (device.app) {
    bridgeUrlClick(event, helpUrl, 'external')
  }
}
const handleAccessibilitySettingsButtonClick = () => {
  trackEvent('Access Request', 'Clicked accessibility settings button')
  emit('open-accessibility-settings')
}
const handleChangeLocale = (lang: string) => {
  trackEvent('Access Request', 'Changed language', null, null, { lang })
}
</script>

<template>
  <footer class="w-fit flex items-center justify-between px-4 py-3 gap-1">
    <!-- Left side: Help and Accessibility buttons -->
    <div class="flex gap-2 items-center">
      <OzIconButton
        :label="__('Help and feedback')"
        icon-name="help_outline"
        :size-preset="OzIconButtonSizePreset.H32px"
        :color-scheme="OzIconButtonColorScheme.SecondaryClear"
        :class="[darkMode ? 'text-light-text-100!' : 'text-dark-text-100!']"
        :href="helpUrl"
        data-testid="accessRequestHelpAndFeedbackButton"
        :href-mode="OzIconButtonHrefMode.NewTab"
        @click="handleHelpAndFeedbackButtonClick"
      />
      <OzIconButton
        :label="__('Accessibility settings')"
        icon-name="accessibility_outline"
        :size-preset="OzIconButtonSizePreset.H32px"
        :color-scheme="OzIconButtonColorScheme.SecondaryClear"
        :class="[darkMode ? 'text-light-text-100!' : 'text-dark-text-100!']"
        data-testid="accessRequestAccessibilitySettingsButton"
        @click="handleAccessibilitySettingsButtonClick"
      />
    </div>

    <!-- Right side: Language selector -->
    <section :aria-label="__('Language selection')">
      <LocaleSelector
        :dark-mode="darkMode"
        :should-bold-dropdown-button-text="true"
        :button-color-scheme="OzDropdownButtonColorScheme.SecondaryClear"
        @change-locale="handleChangeLocale"
      />
    </section>
  </footer>
</template>
